"use client";

import { c as _c } from "react/compiler-runtime";
import { useParams, useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import { type ReactNode, useMemo, useRef, useState } from "react";
import { useAuth } from "@/components/auth/Auth";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import type { ApiWaveCuration } from "@/generated/models/ApiWaveCuration";
import { isOwnProfileRoute } from "@/helpers/ProfileHelpers";
import { useIdentity } from "@/hooks/useIdentity";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useProfileWave } from "@/hooks/useProfileWave";
import { useProfileWaveMutation } from "@/hooks/useProfileWaveMutation";
import { useWaveById } from "@/hooks/useWaveById";
import { useWaveCurations } from "@/hooks/waves/useWaveCurations";
import { useClickAway, useKeyPressEvent } from "react-use";
import UserPageProfileWaveContent from "./UserPageProfileWaveContent";
import UserPageProfileWavePicker from "./UserPageProfileWavePicker";
import { type ProfileIdentitySummary, useProfileMasonryContainerWidth } from "./UserPageProfileWaveMasonry";
import { getOfficialWaveMetadataLabel, getProfileCurationTitle, getProfileIdentityKey, getWaveHref, isUnavailableWaveError, resolveProfileCuration } from "./userPageProfileWave.helpers";
import { InfoPanel, LoadingPanel, OfficialWaveSummary, RetryButton } from "./UserPageProfileWaveShared";
import CircleLoader from "@/components/distribution-plan-tool/common/CircleLoader";
import type { ApiProfileWaveResponse } from "@/services/api/profile-wave-api";
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
type CurationPickerVariant = "dropdown" | "mobile-sheet";
const MyStreamWaveCurationCreateDialog = dynamic(() => import("@/components/brain/my-stream/tabs/MyStreamWaveCurationCreateDialog"), {
  loading: () => null
});
const UserPageProfileWaveQuickPostDialog = dynamic(() => import("./UserPageProfileWaveQuickPostDialog"), {
  loading: () => null
});
type ProfileCurationPickerProps = {
  readonly curations: readonly ApiWaveCuration[];
  readonly selectedCurationId: string | null;
  readonly submittingCurationId: string | null;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly isFetching: boolean;
  readonly onRetry: () => void;
  readonly onSelectCuration: (curationId: string) => void;
  readonly variant: CurationPickerVariant;
};
function ProfileCurationPicker(t0) {
  const $ = _c(30);
  const {
    curations,
    selectedCurationId,
    submittingCurationId,
    isLoading,
    isError,
    isFetching,
    onRetry,
    onSelectCuration,
    variant
  } = t0;
  const isMobileSheet = variant === "mobile-sheet";
  const wrapperClassName = isMobileSheet ? "tw-px-4 sm:tw-px-6" : "tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-py-2 tw-shadow-2xl";
  const rowPadding = isMobileSheet ? "tw-px-4 tw-py-3" : "tw-px-3 tw-py-2.5";
  const isAnySubmitting = submittingCurationId !== null;
  let t1;
  if ($[0] !== isAnySubmitting || $[1] !== rowPadding) {
    t1 = t2 => {
      const {
        id,
        label,
        isSelected,
        isSubmitting,
        onClick
      } = t2;
      let trailingContent = null;
      if (isSubmitting) {
        trailingContent = <CircleLoader />;
      } else {
        if (isSelected) {
          trailingContent = <CheckCircleIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-emerald-400" />;
        }
      }
      return <button key={id} type="button" onClick={onClick} disabled={isAnySubmitting || isSelected} className={`tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-x-3 tw-rounded-xl tw-border-0 tw-text-left tw-font-medium tw-text-white tw-transition tw-duration-200 tw-ease-out focus:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-inset focus-visible:tw-ring-primary-400 disabled:tw-cursor-default ${rowPadding} ${isSelected ? "tw-bg-white/10" : "tw-bg-transparent desktop-hover:hover:tw-bg-white/5"}`}><span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm">{label}</span>{trailingContent}</button>;
    };
    $[0] = isAnySubmitting;
    $[1] = rowPadding;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const renderRow = t1;
  let content;
  if (isLoading) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3 tw-text-sm tw-text-iron-500"><CircleLoader /><span>Loading curations...</span></div>;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    content = t2;
  } else {
    if (isError) {
      let t2;
      if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = <span>Unable to load curations.</span>;
        $[4] = t2;
      } else {
        t2 = $[4];
      }
      let t3;
      if ($[5] !== isFetching || $[6] !== onRetry) {
        t3 = <div className="tw-flex tw-flex-col tw-items-start tw-gap-3 tw-px-3 tw-py-3 tw-text-sm tw-text-iron-500">{t2}<RetryButton isLoading={isFetching} onClick={onRetry} /></div>;
        $[5] = isFetching;
        $[6] = onRetry;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      content = t3;
    } else {
      if (curations.length === 0) {
        let t2;
        if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <p className="tw-mb-0 tw-px-3 tw-py-3 tw-text-sm tw-text-iron-500">This wave has no curations yet.</p>;
          $[8] = t2;
        } else {
          t2 = $[8];
        }
        content = t2;
      } else {
        let t2;
        if ($[9] !== curations || $[10] !== onSelectCuration || $[11] !== renderRow || $[12] !== selectedCurationId || $[13] !== submittingCurationId) {
          let t3;
          if ($[15] !== onSelectCuration || $[16] !== renderRow || $[17] !== selectedCurationId || $[18] !== submittingCurationId) {
            t3 = curation => renderRow({
              id: curation.id,
              label: curation.name,
              isSelected: selectedCurationId === curation.id,
              isSubmitting: submittingCurationId === curation.id,
              onClick: () => onSelectCuration(curation.id)
            });
            $[15] = onSelectCuration;
            $[16] = renderRow;
            $[17] = selectedCurationId;
            $[18] = submittingCurationId;
            $[19] = t3;
          } else {
            t3 = $[19];
          }
          t2 = curations.map(t3);
          $[9] = curations;
          $[10] = onSelectCuration;
          $[11] = renderRow;
          $[12] = selectedCurationId;
          $[13] = submittingCurationId;
          $[14] = t2;
        } else {
          t2 = $[14];
        }
        let t3;
        if ($[20] !== t2) {
          t3 = <>{t2}</>;
          $[20] = t2;
          $[21] = t3;
        } else {
          t3 = $[21];
        }
        content = t3;
      }
    }
  }
  let t2;
  if ($[22] !== isMobileSheet) {
    t2 = !isMobileSheet && <p className="tw-mb-0 tw-px-3 tw-pb-1 tw-pt-2 tw-text-xs tw-font-semibold tw-uppercase tw-text-iron-500">Profile curation</p>;
    $[22] = isMobileSheet;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  let t3;
  if ($[24] !== content || $[25] !== t2) {
    t3 = <div className="tw-flex tw-flex-col tw-gap-1 tw-px-1.5">{t2}{content}</div>;
    $[24] = content;
    $[25] = t2;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  let t4;
  if ($[27] !== t3 || $[28] !== wrapperClassName) {
    t4 = <section className={wrapperClassName}>{t3}</section>;
    $[27] = t3;
    $[28] = wrapperClassName;
    $[29] = t4;
  } else {
    t4 = $[29];
  }
  return t4;
}
function ProfileCurationPickerPanel(t0) {
  const $ = _c(5);
  let pickerProps;
  let show;
  if ($[0] !== t0) {
    ({
      show,
      ...pickerProps
    } = t0);
    $[0] = t0;
    $[1] = pickerProps;
    $[2] = show;
  } else {
    pickerProps = $[1];
    show = $[2];
  }
  if (!show) {
    return null;
  }
  let t1;
  if ($[3] !== pickerProps) {
    t1 = <ProfileCurationPicker {...pickerProps} />;
    $[3] = pickerProps;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function ProfileCurationMobileDialog(t0) {
  const $ = _c(11);
  let isOpen;
  let onClose;
  let pickerProps;
  let show;
  if ($[0] !== t0) {
    ({
      show,
      isOpen,
      onClose,
      ...pickerProps
    } = t0);
    $[0] = t0;
    $[1] = isOpen;
    $[2] = onClose;
    $[3] = pickerProps;
    $[4] = show;
  } else {
    isOpen = $[1];
    onClose = $[2];
    pickerProps = $[3];
    show = $[4];
  }
  if (!show) {
    return null;
  }
  let t1;
  if ($[5] !== pickerProps) {
    t1 = <ProfileCurationPicker {...pickerProps} variant="mobile-sheet" />;
    $[5] = pickerProps;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== isOpen || $[8] !== onClose || $[9] !== t1) {
    t2 = <MobileWrapperDialog title="Switch curation" isOpen={isOpen} onClose={onClose}>{t1}</MobileWrapperDialog>;
    $[7] = isOpen;
    $[8] = onClose;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
const isDesktopCurationPickerOpen = ({
  canSwitchOfficialCuration,
  isDesktopChangeWaveMenu,
  isChangeCurationOpen
}: {
  readonly canSwitchOfficialCuration: boolean;
  readonly isDesktopChangeWaveMenu: boolean;
  readonly isChangeCurationOpen: boolean;
}): boolean => canSwitchOfficialCuration && isDesktopChangeWaveMenu && isChangeCurationOpen;
const shouldRenderMobileCurationPicker = ({
  canSwitchOfficialCuration,
  isDesktopChangeWaveMenu
}: {
  readonly canSwitchOfficialCuration: boolean;
  readonly isDesktopChangeWaveMenu: boolean;
}): boolean => canSwitchOfficialCuration && !isDesktopChangeWaveMenu;
const getProfileCurationAddPostHandler = ({
  profileCuration,
  onAddPost
}: {
  readonly profileCuration: ApiWaveCuration | null;
  readonly onAddPost: () => void;
}): (() => void) | undefined => profileCuration === null ? undefined : onAddPost;
export default function UserPageProfileWave({
  profile: initialProfile
}: {
  readonly profile: ApiIdentity;
}) {
  const params = useParams();
  const router = useRouter();
  const handleOrWallet = params["user"]?.toString() ?? "";
  const {
    containerRef,
    containerWidth
  } = useProfileMasonryContainerWidth();
  const {
    connectedProfile,
    activeProfileProxy
  } = useAuth();
  const {
    profile
  } = useIdentity({
    handleOrWallet,
    initialProfile
  });
  const changeWaveDropdownRef = useRef<HTMLDivElement | null>(null);
  const changeCurationDropdownRef = useRef<HTMLDivElement | null>(null);
  const changeWaveButtonRef = useRef<HTMLButtonElement | null>(null);
  const changeCurationButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isChangeWaveOpen, setIsChangeWaveOpen] = useState(false);
  const [isChangeCurationOpen, setIsChangeCurationOpen] = useState(false);
  const [isCreateCurationOpen, setIsCreateCurationOpen] = useState(false);
  const [isQuickPostOpen, setIsQuickPostOpen] = useState(false);
  const [submittingWaveId, setSubmittingWaveId] = useState<string | null>(null);
  const [submittingCurationId, setSubmittingCurationId] = useState<string | null>(null);
  const isDesktopChangeWaveMenu = useMediaQuery("(min-width: 1024px)");
  const resolvedProfile = profile ?? initialProfile;
  const profileIdentityKey = getProfileIdentityKey(resolvedProfile) ?? handleOrWallet;
  const hasCreatedProfile = Boolean(resolvedProfile.handle?.trim());
  const initialProfileWave = useMemo<ApiProfileWaveResponse>(() => ({
    profile_wave_id: resolvedProfile.profile_wave_id,
    profile_curation_id: null
  }), [resolvedProfile.profile_wave_id]);
  const {
    data: profileWave
  } = useProfileWave({
    identity: profileIdentityKey,
    initialProfileWave
  });
  const isOwnProfile = isOwnProfileRoute({
    connectedProfile,
    handleOrWallet
  });
  const canManageOwnOfficialWave = isOwnProfile && !activeProfileProxy;
  const profileWaveId = profileWave?.profile_wave_id ?? null;
  const profileCurationId = profileWave?.profile_curation_id ?? null;
  const profileIdentityForMasonry: ProfileIdentitySummary = {
    id: resolvedProfile.id,
    handle: resolvedProfile.handle,
    primary_address: resolvedProfile.primary_wallet
  };
  const {
    updateProfileWave,
    clearSelectedProfileWave,
    isPending,
    pendingAction
  } = useProfileWaveMutation(resolvedProfile);
  const {
    data: curations,
    isLoading: areCurationsLoading,
    isError: areCurationsError,
    isFetching: areCurationsFetching,
    refetch: refetchCurations
  } = useWaveCurations({
    waveId: profileWaveId ?? "",
    enabled: Boolean(profileWaveId)
  });
  const {
    wave,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useWaveById(profileWaveId);
  const profileCuration = resolveProfileCuration(curations ?? [], profileCurationId);
  const waveHref = wave ? getWaveHref(wave, profileCuration?.id ?? null) : null;
  const hasLoadedCurations = curations !== undefined;
  const canSwitchOfficialCuration = canManageOwnOfficialWave && (curations?.length ?? 0) > 0;
  const showDesktopCurationPicker = isDesktopCurationPickerOpen({
    canSwitchOfficialCuration,
    isDesktopChangeWaveMenu,
    isChangeCurationOpen
  });
  const showMobileCurationPicker = shouldRenderMobileCurationPicker({
    canSwitchOfficialCuration,
    isDesktopChangeWaveMenu
  });
  const openQuickPost = () => setIsQuickPostOpen(true);
  const addPostHandler = getProfileCurationAddPostHandler({
    profileCuration,
    onAddPost: openQuickPost
  });
  useClickAway(changeWaveDropdownRef, () => {
    if (isDesktopChangeWaveMenu && isChangeWaveOpen) {
      setIsChangeWaveOpen(false);
    }
  });
  useClickAway(changeCurationDropdownRef, () => {
    if (isDesktopChangeWaveMenu && isChangeCurationOpen) {
      setIsChangeCurationOpen(false);
    }
  });
  useKeyPressEvent("Escape", () => {
    if (isChangeWaveOpen) {
      setIsChangeWaveOpen(false);
    }
    if (isChangeCurationOpen) {
      setIsChangeCurationOpen(false);
    }
  });
  const openWave = () => {
    if (!waveHref) {
      return;
    }
    router.push(waveHref, {
      scroll: false
    });
  };
  const retryOfficialWaveLoad = async () => {
    await refetch();
  };
  const retryCurationsLoad = async () => {
    await refetchCurations();
  };
  const selectOfficialCuration = async (curationId: string) => {
    if (!profileWaveId) {
      return;
    }
    setSubmittingCurationId(curationId);
    try {
      const updatedProfile = await updateProfileWave(profileWaveId, curationId);
      if (updatedProfile) {
        setIsChangeCurationOpen(false);
      }
    } finally {
      setSubmittingCurationId(null);
    }
  };
  const handleRemoveOfficialWave = async () => {
    await removeOfficialWave();
  };
  const removeOfficialWave = async () => {
    await clearSelectedProfileWave();
  };
  const selectOfficialWave = async (waveId: string) => {
    setSubmittingWaveId(waveId);
    try {
      const updatedProfile = await updateProfileWave(waveId);
      if (updatedProfile) {
        setIsChangeWaveOpen(false);
        setIsChangeCurationOpen(false);
      }
    } finally {
      setSubmittingWaveId(null);
    }
  };
  if (!profileWaveId) {
    return <UserPageProfileWavePicker title="Choose your featured wave" identity={profileIdentityKey} isOwnProfile={isOwnProfile} hasCreatedProfile={hasCreatedProfile} hasActiveProfileProxy={Boolean(activeProfileProxy)} selectedWaveId={null} submittingWaveId={submittingWaveId} onSelectWave={selectOfficialWave} />;
  }
  if (isUnavailableWaveError(error)) {
    return <InfoPanel title="Featured wave unavailable" message="The featured wave behind this tab could not be loaded. It may have been removed or is no longer accessible." actions={canManageOwnOfficialWave ? <button type="button" onClick={handleRemoveOfficialWave} disabled={isPending} aria-label="Unset featured wave" title="Unset featured wave" className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-transparent tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-zinc-500 tw-transition tw-duration-300 tw-ease-out disabled:tw-cursor-not-allowed disabled:tw-text-iron-600 desktop-hover:hover:tw-border-rose-500/20 desktop-hover:hover:tw-bg-rose-500/10 desktop-hover:hover:tw-text-rose-400">
              {isPending ? <CircleLoader /> : <XMarkIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />}
              <span className="tw-hidden sm:tw-inline">Unset</span>
            </button> : undefined} />;
  }
  if (isLoading) {
    return <LoadingPanel label="Loading featured wave..." />;
  }
  if (isError || !wave) {
    return <InfoPanel title="Unable to load featured wave" message="There was a temporary problem loading this profile curation." actions={<RetryButton isLoading={isFetching} onClick={retryOfficialWaveLoad} />} />;
  }
  return <div className="tw-space-y-5">
      <div className="tw-relative">
        <OfficialWaveSummary waveName={wave.name} metadataLabel={getOfficialWaveMetadataLabel(wave)} profileCurationLabel={profileCuration ? getProfileCurationTitle(profileCuration) : null} canManageOwnOfficialWave={canManageOwnOfficialWave} changeWaveDropdown={canManageOwnOfficialWave && isDesktopChangeWaveMenu && isChangeWaveOpen ? <UserPageProfileWavePicker identity={profileIdentityKey} isOwnProfile={true} hasCreatedProfile={hasCreatedProfile} hasActiveProfileProxy={false} selectedWaveId={profileWaveId} submittingWaveId={submittingWaveId} onSelectWave={selectOfficialWave} variant="dropdown" /> : undefined} changeCurationDropdown={<ProfileCurationPickerPanel show={showDesktopCurationPicker} curations={curations ?? []} selectedCurationId={profileCuration?.id ?? null} submittingCurationId={submittingCurationId} isLoading={areCurationsLoading} isError={areCurationsError} isFetching={areCurationsFetching} onRetry={retryCurationsLoad} onSelectCuration={curationId_0 => selectOfficialCuration(curationId_0)} variant="dropdown" />} changeWaveDropdownRef={changeWaveDropdownRef} changeCurationDropdownRef={changeCurationDropdownRef} changeWaveButtonRef={changeWaveButtonRef} changeCurationButtonRef={changeCurationButtonRef} isChangeWaveOpen={isChangeWaveOpen} isChangeCurationOpen={isChangeCurationOpen} isRemoving={isPending && pendingAction === "clear"} isChangingCuration={submittingCurationId !== null} showChangeCuration={canSwitchOfficialCuration} onOpenWave={openWave} onAddPost={addPostHandler} onOpenChangeWave={() => {
        setIsChangeCurationOpen(false);
        setIsChangeWaveOpen(open => !open);
      }} onOpenChangeCuration={() => {
        setIsChangeWaveOpen(false);
        setIsChangeCurationOpen(open_0 => !open_0);
      }} onRemoveWave={handleRemoveOfficialWave} />
      </div>

      {!isDesktopChangeWaveMenu && canManageOwnOfficialWave && <MobileWrapperDialog title="Switch wave" isOpen={isChangeWaveOpen} onClose={() => setIsChangeWaveOpen(false)}>
          <UserPageProfileWavePicker identity={profileIdentityKey} isOwnProfile={true} hasCreatedProfile={hasCreatedProfile} hasActiveProfileProxy={false} selectedWaveId={profileWaveId} submittingWaveId={submittingWaveId} onSelectWave={selectOfficialWave} variant="mobile-sheet" />
        </MobileWrapperDialog>}

      <ProfileCurationMobileDialog show={showMobileCurationPicker} isOpen={isChangeCurationOpen} onClose={() => setIsChangeCurationOpen(false)} curations={curations ?? []} selectedCurationId={profileCuration?.id ?? null} submittingCurationId={submittingCurationId} isLoading={areCurationsLoading} isError={areCurationsError} isFetching={areCurationsFetching} onRetry={retryCurationsLoad} onSelectCuration={curationId_1 => selectOfficialCuration(curationId_1)} />

      {isCreateCurationOpen && canManageOwnOfficialWave && <MyStreamWaveCurationCreateDialog wave={wave} isOpen={isCreateCurationOpen} onClose={() => setIsCreateCurationOpen(false)} onSaved={curation => selectOfficialCuration(curation.id)} />}

      {isQuickPostOpen && canManageOwnOfficialWave && profileCuration && <UserPageProfileWaveQuickPostDialog wave={wave} curation={profileCuration} isOpen={isQuickPostOpen} onClose={() => setIsQuickPostOpen(false)} />}

      <div ref={containerRef} className="tw-min-w-0 tw-flex-1">
        <UserPageProfileWaveContent canManageOwnOfficialWave={canManageOwnOfficialWave} containerWidth={containerWidth} onCreateCuration={() => setIsCreateCurationOpen(true)} profileIdentity={profileIdentityForMasonry} areCurationsError={areCurationsError} areCurationsFetching={areCurationsFetching} areCurationsLoading={areCurationsLoading} hasLoadedCurations={hasLoadedCurations} onRetryCurations={retryCurationsLoad} profileCuration={profileCuration} wave={wave} />
      </div>
    </div>;
}